import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import iconReversed from '../assets/brand/icon-reversed.svg';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [talentOpen, setTalentOpen] = useState(false);
  const talentRef = useRef(null);

  // close desktop dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (talentRef.current && !talentRef.current.contains(e.target)) setTalentOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate('/');
  };

  const closeMenu = () => {
    setMobileOpen(false);
    setTalentOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 border-b" style={{ backgroundColor: 'rgba(15,41,66,0.97)', backdropFilter: 'blur(16px)', borderColor: 'rgba(240, 235, 226, 0.08)' }}>
      <div className="px-6 md:px-12 lg:px-24 py-5">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" onClick={closeMenu} data-testid="nav-logo">
            <img src={iconReversed} alt="" aria-hidden="true" className="h-12 w-auto" width="50" height="48" />
            <div
              className="flex flex-col justify-center"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              <span
                className="font-semibold tracking-tight"
                style={{ color: '#F0EBE2', fontSize: '28px', lineHeight: '26px', letterSpacing: '-0.01em' }}
              >
                Culture
              </span>
              <span
                className="font-semibold tracking-tight"
                style={{ color: '#D4820A', fontSize: '28px', lineHeight: '26px', letterSpacing: '-0.01em' }}
              >
                Careers
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/browse" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-browse">
              Browse Jobs
            </Link>
            <div className="relative" ref={talentRef}>
              <button
                onClick={() => setTalentOpen((v) => !v)}
                onMouseEnter={() => setTalentOpen(true)}
                className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-white transition-colors"
                data-testid="nav-talent"
                aria-haspopup="true"
                aria-expanded={talentOpen}
              >
                Talent Network
                <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
              {talentOpen && (
                <div
                  className="absolute left-0 top-full mt-3 w-72 rounded-lg shadow-xl overflow-hidden"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE2' }}
                  onMouseLeave={() => setTalentOpen(false)}
                  data-testid="nav-talent-dropdown"
                >
                  <Link
                    to="/talent/professional"
                    onClick={closeMenu}
                    className="block px-5 py-3 hover:bg-[#F0EBE2] transition-colors"
                    style={{ borderBottom: '1px solid #F0EBE2' }}
                    data-testid="nav-talent-professional"
                  >
                    <p className="text-sm font-medium" style={{ color: '#0F2942', fontFamily: 'Cormorant Garamond, serif', fontSize: '17px' }}>
                      Professional Talent Network
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#0F2942' }}>
                      Curators, designers, producers and more
                    </p>
                  </Link>
                  <Link
                    to="/talent/crew"
                    onClick={closeMenu}
                    className="block px-5 py-3 hover:bg-[#F0EBE2] transition-colors"
                    style={{ borderBottom: '1px solid #F0EBE2' }}
                    data-testid="nav-talent-crew"
                  >
                    <p className="text-sm font-medium" style={{ color: '#0F2942', fontFamily: 'Cormorant Garamond, serif', fontSize: '17px' }}>
                      Event &amp; Festival Crew Network
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#0F2942' }}>
                      Photographers, sound, stage, hospitality
                    </p>
                  </Link>
                  <Link
                    to="/talent/why-join"
                    onClick={closeMenu}
                    className="block px-5 py-3 hover:bg-[#F0EBE2] transition-colors"
                    style={{ backgroundColor: '#F0EBE2' }}
                    data-testid="nav-talent-why-join"
                  >
                    <p className="text-sm font-medium" style={{ color: '#D4820A' }}>
                      Why Join?
                    </p>
                  </Link>
                </div>
              )}
            </div>
            <Link to="/post-job" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-post-job">
              Post a Role
            </Link>
            <Link to="/services" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-services">
              Services
            </Link>
            <Link to="/about" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-about">
              About
            </Link>
            <Link
              to="/hire"
              className="text-sm font-bold hover:opacity-90 transition-colors px-4 py-1.5 rounded-none"
              style={{ backgroundColor: '#D4820A', color: '#0F2942', border: '1.5px solid #D4820A' }}
              data-testid="nav-hire"
            >
              Hire Through Us
            </Link>
            {user && user.role === 'admin' && (
              <>
                <Link to="/admin" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-admin">
                  Admin
                </Link>
                <Button onClick={handleLogout} variant="ghost" className="text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg" data-testid="logout-button">
                  Logout
                </Button>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileOpen ? (
              <X className="w-6 h-6 text-white" strokeWidth={1.5} />
            ) : (
              <Menu className="w-6 h-6 text-white" strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden px-6 pb-6 space-y-1" style={{ backgroundColor: 'rgba(15,41,66,0.99)' }} data-testid="mobile-menu">
          <Link to="/browse" onClick={closeMenu} className="block py-3 text-base font-medium text-white/80 hover:text-white transition-colors" style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.06)' }}>
            Browse Jobs
          </Link>
          <div style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.06)' }}>
            <p className="pt-3 pb-1.5 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: 'rgba(240, 235, 226, 0.45)' }}>
              Talent Network
            </p>
            <Link to="/talent/professional" onClick={closeMenu} className="block py-2.5 pl-3 text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-mobile-talent-professional">
              Professional Talent Network
            </Link>
            <Link to="/talent/crew" onClick={closeMenu} className="block py-2.5 pl-3 text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-mobile-talent-crew">
              Event &amp; Festival Crew Network
            </Link>
            <Link to="/talent/why-join" onClick={closeMenu} className="block py-2.5 pb-3 pl-3 text-sm font-medium hover:text-white transition-colors" style={{ color: '#D4820A' }} data-testid="nav-mobile-talent-why-join">
              Why Join?
            </Link>
          </div>
          <Link to="/post-job" onClick={closeMenu} className="block py-3 text-base font-medium text-white/80 hover:text-white transition-colors" style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.06)' }}>
            Post a Role
          </Link>
          <Link to="/services" onClick={closeMenu} className="block py-3 text-base font-medium text-white/80 hover:text-white transition-colors" style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.06)' }}>
            Services
          </Link>
          <Link to="/about" onClick={closeMenu} className="block py-3 text-base font-medium text-white/80 hover:text-white transition-colors" style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.06)' }}>
            About
          </Link>
          <Link
            to="/hire"
            onClick={closeMenu}
            className="block mt-3 py-3 text-base font-bold text-white text-center hover:opacity-90 transition-colors"
            style={{ backgroundColor: '#D4820A', border: '1.5px solid #D4820A' }}
          >
            Hire Through Us
          </Link>
          {user && user.role === 'admin' && (
            <>
              <Link to="/admin" onClick={closeMenu} className="block py-3 text-base font-medium text-white/80 hover:text-white transition-colors" style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.06)' }}>
                Admin
              </Link>
              <button onClick={handleLogout} className="block py-3 text-base font-medium text-white/80 hover:text-white transition-colors w-full text-left">
                Logout
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
